/**
 * Append an AgileCO cache-buster query param so that clicking a just-fixed link
 * shows the fresh origin state instead of a stale browser/CDN-cached copy — the
 * exact trap where a redirect is live server-side but the browser still serves a
 * cached 404.
 *
 * Same-origin guard: the buster is only added when `url` is on the same host as
 * `sameOriginAs` (the client's own site). External URLs are returned untouched —
 * some servers treat an unknown `?query` as a different page and return a soft-404,
 * which would make a perfectly good link look broken.
 */
export function withCacheBuster(url: string, sameOriginAs?: string): string {
	if (!url) return url;
	try {
		const target = new URL(url);
		if (sameOriginAs) {
			const site = new URL(sameOriginAs);
			if (target.host !== site.host) return url; // external → leave bare
		}
		target.searchParams.set("agileco_cb", String(Date.now()));
		return target.toString();
	} catch {
		// Not an absolute URL (or unparseable) — leave it exactly as-is.
		return url;
	}
}

/**
 * Click handler for an audited-page `<a>` link. Keeps the rendered `href` bare —
 * so `agileco_cb` is NEVER visible on the page, on hover, or when copying the
 * link — and, only on a plain primary click, opens a cache-busted URL in a new
 * tab so the browser fetches the freshest origin state (Carl's UX condition:
 * append the param on-click, not in the UI).
 *
 * Modified/non-primary clicks (⌘/Ctrl/Shift/Alt or middle-click) fall through
 * to the native `target="_blank"` behavior — they open the bare href, honoring
 * the user's explicit gesture and avoiding a duplicate tab.
 */
export function openWithCacheBuster(
	event: import("react").MouseEvent<HTMLAnchorElement>,
	url: string | null | undefined,
	sameOriginAs?: string,
): void {
	if (!url) return;
	if (
		event.defaultPrevented ||
		event.button !== 0 ||
		event.metaKey ||
		event.ctrlKey ||
		event.shiftKey ||
		event.altKey
	) {
		return;
	}
	event.preventDefault();
	window.open(withCacheBuster(url, sameOriginAs), "_blank", "noopener,noreferrer");
}

/**
 * Issue types whose page URL must be opened BARE, with no cache-buster.
 *
 * Redirect rules are matched against the request URL, and most redirect plugins
 * (including our own `agileco_redirects_serve`, which normalizes away the query
 * string only after hashing the path) match on the exact path — so an extra
 * `?agileco_cb=…` can miss every rule and fall straight through to a 404.
 * Observed on allaboutplumbingnc.com (2026-08-13): the drawer's own link 404'd
 * while the bare URL redirected correctly.
 *
 * For these types the redirect IS the thing being inspected, so busting the
 * cache costs us the entire observation. Opening bare is the correct behavior,
 * not a compromise.
 */
const BARE_LINK_ISSUE_TYPES = new Set([
	"redirect",
	"redirect_chain",
	"redirect_loop",
	"links_to_redirect",
]);

export function opensBare(issueType?: string | null): boolean {
	return Boolean(issueType && BARE_LINK_ISSUE_TYPES.has(issueType));
}

/**
 * `openWithCacheBuster`, but leaves the URL untouched for issue types whose
 * redirect would be broken by the extra query param (see {@link opensBare}).
 * Returning without `preventDefault()` lets the anchor's native
 * `target="_blank"` open the bare href.
 */
export function openAuditedPageLink(
	event: import("react").MouseEvent<HTMLAnchorElement>,
	url: string | null | undefined,
	issueType?: string | null,
	sameOriginAs?: string,
): void {
	if (opensBare(issueType)) return;
	openWithCacheBuster(event, url, sameOriginAs);
}
